import { _ } from '@ngx-translate/core';
import { z } from 'zod';
import { PriceViewSchema } from '@fsf/types';

/**
 * API surface for `priceable_type` (slug form, NOT FQCN).
 * Mirrors `apps/api/app/Http/Requests/StorePriceRequest::PRICEABLE_TYPE_MAP`.
 */
export const PRICEABLE_TYPES = ['product', 'ticket', 'additional_product'] as const;
export type PriceableType = (typeof PRICEABLE_TYPES)[number];

export type Price = z.infer<typeof PriceViewSchema>;

export interface PriceOwner {
  type: PriceableType;
  id: number;
}

/**
 * Decimal-string format expected by the BE (`StorePriceRequest`).
 * Accepts integers or up to two decimals, e.g. `"19"`, `"19.00"`, `"14.50"`.
 */
const decimalString = z
  .string()
  .regex(/^\d+(\.\d{1,2})?$/, { message: 'validation.invalid' });

export const StorePriceSchema = z.object({
  priceable_type: z.enum(PRICEABLE_TYPES),
  priceable_id: z.number().int().min(1),
  price: decimalString,
  tax: decimalString.optional().nullable(),
  valid_from: z.string().min(1, { message: 'validation.required' }),
});

export type StorePricePayload = z.infer<typeof StorePriceSchema>;

export const priceTranslations = _([
  'catalog.prices.historyTitle',
  'catalog.prices.addNew',
  'catalog.prices.validFrom',
  'catalog.prices.price',
  'catalog.prices.tax',
  'catalog.prices.current',
  'catalog.prices.deleteConfirmHeader',
  'catalog.prices.deleteConfirmMessage',
  'catalog.prices.created',
  'catalog.prices.deleted',
  'catalog.prices.createError',
  'catalog.prices.deleteError',
  'catalog.prices.noEntries',
  'catalog.prices.cancel',
  'catalog.prices.save',
]);
